import { Button, Badge, message, Tooltip, Card } from 'antd';
import { CalendarOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import ServiceCard from './ServiceCard';
import { openPrintPreview } from '../../../../components/Print';
import { calculateAge } from '../../../../utils/functions';

const DateDetailView = ({
    date,
    dataSource,
    handlePlanChange,
    SetModel,
    isPast,
    isToday,
    roomName,
    roomType,
    formatTime,
    calculateDuration,
    getTimePeriod,
    formikProps,
    patientDetail,
    patientPlanList
}) => {
    const formattedDate = moment(date).format('DD MMMM YYYY');
    const dayName = moment(date).format('dddd');

    // 1. Patient Information
    const customerData = patientPlanList?.customerId || {
        name: patientPlanList?.customerName,
        email: patientPlanList?.customerEmail,
        age: patientPlanList?.customerAge,
        phoneNo: patientPlanList?.customerPhoneNo,
        gender: patientPlanList?.customerGender,
        birthDate: patientPlanList?.customerBirthdate,
        registrationNo: patientPlanList?.registrationNo,
    };
    const displayData = patientDetail || customerData;

    const patientSection = {
        title: 'Patient Information',
        fields: [
            { label: 'Patient Name', value: displayData?.name || '—' },
            { label: 'Case Paper / Registration No', value: displayData?.registrationNo || '—' },
            { label: 'Phone Number', value: displayData?.phoneNo || '—' },
            { label: 'Email Address', value: displayData?.email || '—' },
            { label: 'Gender', value: displayData?.gender || '—' },
            { label: 'Date of Birth', value: displayData?.birthDate ? moment(displayData.birthDate).format('DD MMM YYYY') : '—' },
            { label: 'Age', value: displayData?.birthDate ? calculateAge(displayData.birthDate) : displayData?.age || '—' },
            { label: 'Room Name', value: roomName || '—' },
            { label: 'Room Type', value: roomType || '—' },
        ]
    };

    // 2. Treatment Schedule Table Data
    const tableData = [];
    const periodsList = ['Morning', 'Afternoon', 'Evening', 'Night'];
    periodsList.forEach(period => {
        const servicesInPeriod = dataSource.filter(s => getTimePeriod(s.startTime).period === period);
        if (servicesInPeriod.length > 0) {
            servicesInPeriod.forEach((s, idx) => {
                const cellData = s[date];
                tableData.push([
                    idx === 0 ? period : '',
                    s.serviceName,
                    `${formatTime(s.startTime)} - ${formatTime(s.endTime)}`,
                    cellData?.medicineValue || 'Not Started'
                ]);
            });
        }
    });

    const scheduleSection = {
        title: "Today's Treatment Schedule",
        type: 'table',
        headers: ['Period', 'Service/Medicine', 'Time Slot', 'Status'],
        data: tableData
    };

    // 3. Clinical Notes & Doctor Rounds Summary
    const dailyRoundData = formikProps?.values?.doctorRoundForm?.[date];
    const displayRounds = [];
    const roundPrintFields = [];

    if (dailyRoundData) {
        ['morning', 'evening', 'emergency'].forEach(type => {
            const data = dailyRoundData[type];
            if (data && (data.note || data.temperature || data.pulse || data.BPMorning || data.BPEvening)) {
                const roundLabel = type.charAt(0).toUpperCase() + type.slice(1);
                const bp = type === 'evening' ? data.BPEvening : data.BPMorning;

                // For Print - Round Summary
                const vitalsText = `BP: ${bp || '-'}, Pulse: ${data.pulse || '-'}, SpO2: ${data.spO2 || '-'}%, Temp: ${data.temperature || '-'}°F, RR: ${data.respiratoryRate || '-'}`;
                roundPrintFields.push({
                    label: `${roundLabel} Round`,
                    value: `Vitals: ${vitalsText}\nRound Observation: ${data.note || '—'}`
                });

                // For UI Display
                displayRounds.push({
                    type,
                    label: roundLabel,
                    vitals: {
                        bp,
                        pulse: data.pulse,
                        spO2: data.spO2,
                        temp: data.temperature,
                        rr: data.respiratoryRate
                    },
                    note: data.note,
                    icon: type === 'morning' ? '🌅' : type === 'evening' ? '🌆' : '🚨'
                });
            }
        });
    }

    // 4. General Clinical Notes & Diet Plan Notes
    const mainNotes = formikProps?.values?.notes;
    const dietPlanNote = formikProps?.values?.dietPlanNote;
    const clinicalNotesFields = [];

    if (mainNotes) {
        clinicalNotesFields.push({ label: 'General Clinical Notes', value: mainNotes });
    }
    if (dietPlanNote) {
        clinicalNotesFields.push({ label: 'Diet Plan Instructions', value: dietPlanNote });
    }

    const roundPrintSection = {
        title: 'Doctor Rounds Summary',
        fields: roundPrintFields.length > 0 ? roundPrintFields : [{ label: 'Doctor Rounds', value: 'No round data recorded for this date' }]
    };

    const notesPrintSection = {
        title: 'Clinical & Diet Notes',
        fields: clinicalNotesFields.length > 0 ? clinicalNotesFields : [{ label: 'Notes', value: 'No general notes recorded' }]
    };

    const handlePrint = () => {
        openPrintPreview(
            displayData,
            `Daily Treatment Plan - ${formattedDate}`,
            {
                sections: [patientSection, scheduleSection, roundPrintSection, notesPrintSection],
                headerConfig: {
                    hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
                    subtitle: 'Indoor Patient Treatment Record',
                    logo: '/prakruti-logo.png'
                }
            }
        );
    };

    const hasData = (roundType) => {
        const roundData = formikProps?.values?.doctorRoundForm?.[date]?.[roundType];
        if (!roundData) return false;

        return Object.keys(roundData).some(key => {
            const value = roundData[key];
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'number') return true;
            return false;
        });
    };

    const groupedServices = dataSource.reduce((acc, service) => {
        const { period } = getTimePeriod(service.startTime);
        if (!acc[period]) acc[period] = [];
        acc[period].push(service);
        return acc;
    }, {});

    const periodOrder = ['Morning', 'Afternoon', 'Evening', 'Night'];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{
                background: isToday ? '#e6f7ff' : isPast ? '#f5f5f5' : '#fafafa',
                border: isToday ? '2px solid #1890ff' : '1px solid #d9d9d9',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div>
                        <h4 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: '600',
                            color: isToday ? '#1890ff' : isPast ? '#8c8c8c' : '#262626'
                        }}>
                            <CalendarOutlined style={{ marginRight: '8px' }} />
                            {formattedDate}
                        </h4>
                        <p style={{
                            margin: '4px 0 0 28px',
                            fontSize: '13px',
                            color: '#8c8c8c'
                        }}>
                            {dayName} {isToday && '• Today'} {isPast && '• Past Date'}
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {(isToday || isPast) && (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', color: '#8c8c8c', marginRight: '4px' }}>Doctor Rounds:</span>
                                {[
                                    { type: 'morning', label: 'Morning', icon: '🌅', color: '#faad14', pastColor: '#b7b8b9' },
                                    { type: 'evening', label: 'Evening', icon: '🌆', color: '#722ed1', pastColor: '#b7b8b9' },
                                    { type: 'emergency', label: 'Emergency', icon: '🚨', color: '#f5222d', pastColor: '#b7b8b9' }
                                ].map(round => (
                                    <Tooltip key={round.type} title={`Record ${round.label} Round`}>
                                        <Badge dot={hasData(round.type)} color="#52c41a" offset={[-5, 5]}>
                                            <Button
                                                size="small"
                                                onClick={() => SetModel({ date, open: true, isPastDate: isPast, roundType: round.type })}
                                                style={{
                                                    background: round.color,
                                                    borderColor: round.color,
                                                    color: '#fff',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {round.icon} {round.label}
                                            </Button>
                                        </Badge>
                                    </Tooltip>
                                ))}
                            </div>
                        )}

                        <Tooltip title="Print Treatment Plan Preview">
                            <Button
                                icon={<PrinterOutlined />}
                                size="small"
                                type="primary"
                                style={{
                                    background: '#1890ff',
                                    borderColor: '#1890ff',
                                    fontWeight: '600',
                                    marginLeft: '8px'
                                }}
                                onClick={() => {
                                    message.loading(`Preparing print preview for ${formattedDate}...`, 1);
                                    setTimeout(() => {
                                        handlePrint();
                                    }, 500);
                                }}
                            >
                                Print Plan
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {periodOrder.map(period => {
                const services = groupedServices[period];
                if (!services || services.length === 0) return null;

                const periodInfo = getTimePeriod(services[0].startTime);
                return (
                    <div key={period} style={{ marginBottom: '32px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #e8e8e8',
                        }}>
                            <span style={{ fontSize: '24px' }}>{periodInfo.icon}</span>
                            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#262626' }}>{period} Sessions</h5>
                            <Badge count={services.length} style={{ backgroundColor: '#8c8c8c', boxShadow: 'none' }} />
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                            gap: '16px',
                        }}>
                            {services.map((service) => (
                                <ServiceCard
                                    key={service.key || dataSource.indexOf(service)}
                                    service={service}
                                    serviceIndex={dataSource.indexOf(service)}
                                    date={date}
                                    cellData={service[date]}
                                    isPast={isPast}
                                    periodInfo={periodInfo}
                                    formatTime={formatTime}
                                    calculateDuration={calculateDuration}
                                    handlePlanChange={handlePlanChange}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* Clinical Notes & Doctor Rounds UI Section */}
            {displayRounds.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '2px solid #e8e8e8',
                    }}>
                        <span style={{ fontSize: '24px' }}>📋</span>
                        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#262626' }}>Doctor Rounds Summary</h5>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                        gap: '20px',
                    }}>
                        {displayRounds.map((round) => (
                            <Card
                                key={round.type}
                                size="small"
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '18px' }}>{round.icon}</span>
                                        <span>{round.label} Summary</span>
                                    </div>
                                }
                                headStyle={{
                                    background: round.type === 'morning' ? '#fff7e6' : round.type === 'evening' ? '#f9f0ff' : '#fff1f0',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                                style={{
                                    borderRadius: '12px',
                                    border: '1px solid #f0f0f0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    overflow: 'hidden'
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 8px' }}>
                                    {/* Vitals Grid */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '12px',
                                        background: '#fafafa',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase' }}>Blood Pressure</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#262626' }}>{round.vitals.bp || '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase' }}>Pulse Rate</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#262626' }}>{round.vitals.pulse ? `${round.vitals.pulse} bpm` : '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase' }}>SpO2 (%)</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#262626' }}>{round.vitals.spO2 ? `${round.vitals.spO2} %` : '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase' }}>Temperature</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#262626' }}>{round.vitals.temp ? `${round.vitals.temp} °F` : '—'}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c', textTransform: 'uppercase' }}>Resp. Rate</span>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#262626' }}>{round.vitals.rr || '—'}</span>
                                        </div>
                                    </div>

                                    {/* Notes area */}
                                    {round.note && (
                                        <div style={{
                                            padding: '12px',
                                            background: '#fffbe6',
                                            border: '1px solid #ffe58f',
                                            borderRadius: '8px',
                                            position: 'relative'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: '-8px',
                                                left: '12px',
                                                background: '#fffbe6',
                                                padding: '0 4px',
                                                fontSize: '10px',
                                                fontWeight: '600',
                                                color: '#856404',
                                                textTransform: 'uppercase'
                                            }}>
                                                Round Notes
                                            </div>
                                            <div style={{
                                                fontSize: '13px',
                                                color: '#434343',
                                                lineHeight: '1.5',
                                                whiteSpace: 'pre-wrap'
                                            }}>
                                                {round.note}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* General Clinical & Diet Notes Section */}
            {(mainNotes || dietPlanNote) && (
                <div style={{ marginTop: '32px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                        paddingBottom: '8px',
                        borderBottom: '2px solid #e8e8e8',
                    }}>
                        <span style={{ fontSize: '24px' }}>📝</span>
                        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#262626' }}>Clinical & Diet Notes</h5>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {mainNotes && (
                            <Card size="small" title="General Clinical Observations" style={{ borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: '14px', color: '#434343', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {mainNotes}
                                </div>
                            </Card>
                        )}
                        {dietPlanNote && (
                            <Card size="small" title="Diet & Nutrition Instructions" style={{ borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: '14px', color: '#434343', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {dietPlanNote}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateDetailView;
